import React from 'react';

import { Button, Card, Spinner } from '@lightspeed/unified-components-default-consumer-theme/react';
import { T } from '@transifex/react';

import { EmptyLayout } from '../../../shared/components/layout/empty-layout';
import { ReservationSummary } from '../../../shared/components/reservation-summary';
import {
  BusinessLocation,
  ReservationConfirmation,
  RESERVATION_STATUS,
} from '../../../shared/types';
import { ReservationStatusHeader } from '../../../status/components/reservation-status-header';
import { useReservationConfirmationData } from '../../hooks/use-reservation-confirmation.hook';

import { StickyFooter } from './sticky-footer';

interface ReservationConfirmationStepProps {
  businessLocation: BusinessLocation;
  reservationConfirmation: ReservationConfirmation;
  onTryAgain: () => void;
}

export function ReservationConfirmationStep({
  businessLocation,
  reservationConfirmation,
  onTryAgain,
}: ReservationConfirmationStepProps) {
  const {
    partySize,
    utcScheduledAt,
    reservation,
    isReservationLoading,
    isReservationError,
    cancelReservation,
    isCancelling,
  } = useReservationConfirmationData({ businessLocation, reservationConfirmation });

  if (isReservationLoading || !reservation) {
    return (
      <EmptyLayout>
        <div className="flex h-screen items-center justify-center">
          <Spinner customClasses={{ icon: '[&>svg]:size-8' }} />
        </div>
      </EmptyLayout>
    );
  }

  if (isReservationError) {
    return (
      <EmptyLayout>
        <Card customClasses={{ root: 'p-6 text-center mx-auto mt-12 max-w-[600px]' }}>
          <T
            _str="Reservation not found"
            _context="Error when reservation is not found"
            _tags="reservation,errors"
            _key="Widget.Confirmation.ReservationNotFound.title"
          />
        </Card>
      </EmptyLayout>
    );
  }

  const showTryAgain =
    reservation.status === RESERVATION_STATUS.ERROR ||
    reservation.status === RESERVATION_STATUS.NO_SHOW;
  const showCancel = reservation.status === RESERVATION_STATUS.SCHEDULED;

  return (
    <>
      <div className="px-4 py-3">
        <ReservationStatusHeader status={reservation.status} />
        <ReservationSummary
          time={utcScheduledAt}
          partySize={partySize}
          restaurantNote={businessLocation.integrationConfig?.noteToGuest}
          timezone={businessLocation.timezone}
          guestNote={reservation.guestRequest}
        />
      </div>

      {(showTryAgain || showCancel) && (
        <StickyFooter>
          {showTryAgain && (
            <Button
              type="button"
              size="large"
              block
              onClick={onTryAgain}
              customDataAttributes={{ root: { testid: 'try-again-button' } }}
            >
              <T
                _str="Try again"
                _context="Button text to create a new reservation"
                _tags="widget,reservation"
                _key="Widget.Confirmation.CreateNew.button"
              />
            </Button>
          )}
          {showCancel && (
            <Button
              type="button"
              appearance="secondary"
              size="large"
              block
              loading={isCancelling}
              onClick={cancelReservation}
              customDataAttributes={{ root: { testid: 'cancel-reservation-button' } }}
            >
              <T
                _str="Cancel reservation"
                _context="Cancel reservation button text"
                _tags="widget,reservation"
                _key="Widget.Confirmation.Cancel.button"
              />
            </Button>
          )}
        </StickyFooter>
      )}
    </>
  );
}
